import type { Venue } from 'types/domain';

export const actions = {
  FETCH_FAVOURITES: 'FETCH_FAVOURITES',
  FETCH_FAVOURITES_SUCCESS: 'FETCH_FAVOURITES_SUCCESS',
  ADD_FAVOURITE: 'ADD_FAVOURITE',
  REMOVE_FAVOURITE: 'REMOVE_FAVOURITE',
} as const;

// /v1/users/favourites/* returns trimmed-down records rather than the full
// domain objects, so these are their own shapes rather than the canonical
// Venue/Space. Notably a favourite space carries its parent venue inline, which
// the canonical Space does not.
export interface FavouriteVenue extends Pick<Venue, 'id' | 'name'> {
  images?: Venue['images'];
}

export interface FavouriteSpace {
  id: number;
  name: string;
  clinical: boolean;
  venue: Pick<Venue, 'id' | 'name'>;
  image: { thumbnail: string };
}

// The two lists are addressed by the same string the API path uses, which is
// why the reducer writes them by computed key.
export type FavouriteCategory = 'venues' | 'spaces';

export interface FavouritesState {
  venues: FavouriteVenue[];
  spaces: FavouriteSpace[];
  loading?: boolean;
}

export type FavouritesAction =
  | { type: 'FETCH_FAVOURITES' }
  | {
      type: 'FETCH_FAVOURITES_SUCCESS';
      payload: {
        type: FavouriteCategory;
        favourites: FavouriteVenue[] | FavouriteSpace[];
      };
    }
  | {
      type: 'ADD_FAVOURITE';
      payload: {
        type: FavouriteCategory;
        favourite: FavouriteVenue | FavouriteSpace;
      };
    }
  | {
      type: 'REMOVE_FAVOURITE';
      payload: {
        type: FavouriteCategory;
        favourite: { id: number };
      };
    };

export const initialState: FavouritesState = {
  venues: [],
  spaces: [],
};

// Both lists hold `{ id, name }`-shaped records, so the by-name sort and the
// by-id filter below work over either without narrowing on the category.
type Favourite = FavouriteVenue | FavouriteSpace;

const reducer = (
  state: FavouritesState,
  action: FavouritesAction
): FavouritesState => {
  switch (action.type) {
    case actions.FETCH_FAVOURITES: {
      return {
        ...state,
        loading: true,
      };
    }

    case actions.FETCH_FAVOURITES_SUCCESS: {
      const { type } = action.payload;
      const { favourites } = action.payload;

      return {
        ...state,
        loading: false,
        [type]: favourites,
      };
    }

    case actions.ADD_FAVOURITE: {
      const { type } = action.payload;
      const { favourite } = action.payload;
      const current: Favourite[] = state[type];

      return {
        ...state,
        [type]: [...current, favourite].sort((a, b) =>
          a.name.localeCompare(b.name)
        ),
      };
    }

    case actions.REMOVE_FAVOURITE: {
      const { type } = action.payload;
      const favourites: Favourite[] = state[type];
      const { favourite } = action.payload;

      return {
        ...state,
        [type]: favourites.filter(item => item.id !== favourite.id),
      };
    }

    default:
      return state;
  }
};

export default reducer;
